<template>
  <div class="mgx_set_group_container">
    <div class="mgx_top_right_fixed">
      <div class="top_right_btn mgx_action_btn" @click="back">
        {{ t('back') }}
      </div>
    </div>

    <div class="key_group_container">
      <div class="key_group_device">
        <Virtual_devices ref="virtualDevicesRef" @key-click="selectKeyClick" :is-continuous-select="true" :key-class-resolver="resolveKeyGroupClass" :key-style-resolver="resolveKeyGroupStyle" @continuous-select-key="continuousSelectKey" />
        <div class="key_group_message">{{ $t('key_group_message') }}</div>
      </div>

      <div class="key_groups">
        <div class="key_group_item" v-for="[key, value] in KeyGroupInfoMap()" :key="key" @click="setGroup(key)">
          <div class="group_color_indicator" :style="{ background: '#' + value.color.toString(16), border: '2px solid #ffffff' }"></div>
          <div class="group_name">{{ $t(value.groupNameTranslationKey) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { DialogType, enableDialog } from '../cdialog/dialog'
import i18n from '@/languages/i18n'
import { KeyGroup } from '@libs/types/enum'
import { ref } from 'vue'
import { getColorRGBA } from '@/tool/utility'
import { KK } from '@/define/define'

// import { DeviceMagnet } from '../device/device_magnet';
import { KeyGroupInfo, KeyGroupInfoMap } from '@/define/key_group'
import { magnetSetKeyGroup } from '@/service/driver/magnet_key_group'
import { onMounted } from 'vue'
import Virtual_devices from '../device/virtual_devices.vue'
const { t } = i18n.global

const virtualDevicesRef = ref<InstanceType<typeof Virtual_devices> | null>(null)
const isFirstSetKeyGroup = ref(false)
const currentKeyGroup = ref(KeyGroup.none)

const setGroup = async (key: KeyGroup) => {
  if (selectKeys.value.length == 0) {
    enableDialog(t('please_select_key'), DialogType.notice, false)
    return
  }
  currentKeyGroup.value = key
  handleKeysGroup(key)
  await magnetSetKeyGroup.setKeysGroupInfo()
  await magnetSetKeyGroup.handleSetKeyGroupDeadZone(selectKeys.value, key)
  selectKeys.value = []
  virtualDevicesRef.value?.clearContiguousSelection()
  await magnetSetKeyGroup.getKeysGroupInfo()
  enableDialog(t('setting_success'))
}

const handleKeysGroup = (key: KeyGroup) => {
  let groupData = magnetSetKeyGroup.state.keysGroup.get(key)
  if (!groupData) {
    magnetSetKeyGroup.state.keysGroup.set(key, [])
  }

  for (let [keyGroup, _] of magnetSetKeyGroup.state.keysGroup) {
    let data: Set<number> = new Set()
    magnetSetKeyGroup.state.keysGroup.get(keyGroup)?.forEach((kk) => {
      data.add(kk)
    })
    selectKeys.value.forEach((kk) => {
      if (keyGroup != key) {
        data.delete(kk)
      } else {
        data.add(kk)
      }
    })
    if (data.size > 0) {
      magnetSetKeyGroup.state.keysGroup.set(keyGroup, Array.from(data))
    } else {
      magnetSetKeyGroup.state.keysGroup.delete(keyGroup)
    }
  }
}

onMounted(() => {
  if (magnetSetKeyGroup.state.keysGroup.size == 1) {
    isFirstSetKeyGroup.value = true
  }
})

const back = async () => {
  await magnetSetKeyGroup.closeSettings()
}
const selectKeys = ref<KK[]>([])

const selectKeyClick = (kk: KK) => {
  if (selectKeys.value.includes(kk)) {
    selectKeys.value = selectKeys.value.filter((item) => item != kk)
  } else {
    selectKeys.value.push(kk)
  }
}

const continuousSelectKey = (kks: KK[]) => {
  selectKeys.value = Array.from(new Set([...selectKeys.value, ...kks]))
}

const resolveKeyGroupClass = (kk: KK) => {
  return {
    key_group_selected: selectKeys.value.includes(kk),
  }
}

const resolveKeyGroupStyle = (kk: KK) => {
  const backgroundColor = getStyle(kk)
  if (!backgroundColor) {
    return {}
  }

  return {
    backgroundColor,
  }
}

const getKeysGroupColors = (kk: KK): KeyGroupInfo | undefined => {
  let info: KeyGroupInfo | undefined
  for (const [keyGroup, kks] of magnetSetKeyGroup.state.keysGroup) {
    if (kks.includes(kk)) {
      if (keyGroup == magnetSetKeyGroup.state.defaultKeysGroup) {
        return info
      }
      info = KeyGroupInfoMap().get(keyGroup)
      return info
    }
  }
  return info
}

const getStyle = (kk: KK): string | null => {
  const info = getKeysGroupColors(kk)
  if (info && info.color > 0 && !selectKeys.value.includes(kk)) {
    return getColorRGBA(info.color, 0.6)
  }
  return null
}
</script>

<style>
.link {
  text-decoration: none;
  color: #ff3333;
}
</style>

<style lang="less" scoped>
.mgx_set_group_container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 40px;
}

.mgx_top_right_fixed {
  z-index: 10;
}

.key_group_container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
  max-width: 1200px;
  margin-top: 60px;
}

:deep(.device_key.key_group_selected) {
  background-color: rgba(var(--color-brand-1-rgb), 0.7) !important;
  box-shadow: 0 0 15px rgba(var(--color-brand-1-rgb), 0.5) !important;
}

.key_group_device {
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .key_group_message {
    margin-top: 24px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    max-width: 800px;
    text-align: center;
    line-height: 1.6;
  }
}

.key_groups {
  margin-top: 40px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  width: 100%;

  .key_group_item {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px 24px;
    background: var(--glass-ghost-bg);
    backdrop-filter: var(--glass-overlay-blur); /* 调低模糊半径至 4px，匹配设计图的轻透感 */
    border-radius: var(--radius-m);
    cursor: pointer;
    transition: var(--transition-base);
    border: 1px solid rgba(255, 255, 255, 0.4); /* 增强边框亮度 */
    min-width: 160px;

    .group_color_indicator {
      width: 16px;
      height: 16px;
      border-radius: var(--radius-s);
      margin-right: 12px;
      flex-shrink: 0;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    }

    .group_name {
      font-size: 14px; /* 保留较小字号 */
      font-weight: 700;
      color: var(--action-btn-ghost-text);
      letter-spacing: 0.5px;
    }

    &:hover {
      transform: translateY(-4px);
      background: rgba(255, 255, 255, 0.9); /* 还原为原始 hover 背景 */
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
      border-color: var(--brand-color-pure); /* 还原为原始 hover 边框 */

      .group_name {
        color: var(--brand-color-pure);
      }
    }

    &:active {
      transform: translateY(-1px);
    }
  }
}
</style>
